import type { Customer, Reward, LeaderboardEntry } from '../types'
import { useStorage } from './storage'

// Initialize data if not present
export const initializeData = () => {
  const { setItem } = useStorage()
  
  // Check if we have demo data, if not, set defaults
  if (!localStorage.getItem('demoDataInitialized')) {

  const defaultWorkspaceId = 'integration-test-workspace'

  const defaultCustomers: Customer[] = [
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c1',
      workspace_id: defaultWorkspaceId,
      username: 'sally.shopper',
      display_name: 'Sally Shopper',
      points: 5200,
      avatar_url: 'https://i.pravatar.cc/150?u=sally.shopper',
      tags: { 
        membership_type: 'Gold',
        age: '45',
        segment: 'professional'
      }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c2',
      workspace_id: defaultWorkspaceId,
      username: 'barry.bargains',
      display_name: 'Barry Bargains',
      points: 7800,
      avatar_url: 'https://i.pravatar.cc/150?u=barry.bargains',
      tags: { 
        membership_type: 'Platinum',
        age: '38',
        segment: 'advanced'
      }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c3',
      workspace_id: defaultWorkspaceId,
      username: 'penny.pricer',
      display_name: 'Penny Pricer',
      points: 3400,
      avatar_url: 'https://i.pravatar.cc/150?u=penny.pricer',
      tags: { membership_type: 'Silver' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c4',
      workspace_id: defaultWorkspaceId,
      username: 'discount.dave',
      display_name: 'Discount Dave',
      points: 9100,
      avatar_url: 'https://i.pravatar.cc/150?u=discount.dave',
      tags: { membership_type: 'Gold' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c5',
      workspace_id: defaultWorkspaceId,
      username: 'premium.pete',
      display_name: 'Premium Pete',
      points: 12500,
      avatar_url: 'https://i.pravatar.cc/150?u=premium.pete',
      tags: { membership_type: 'Platinum' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c6',
      workspace_id: defaultWorkspaceId,
      username: 'thrifty.tina',
      display_name: 'Thrifty Tina',
      points: 2800,
      avatar_url: 'https://i.pravatar.cc/150?u=thrifty.tina',
      tags: { membership_type: 'Silver' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c7',
      workspace_id: defaultWorkspaceId,
      username: 'value.victor',
      display_name: 'Value Victor',
      points: 6200,
      avatar_url: 'https://i.pravatar.cc/150?u=value.victor',
      tags: { membership_type: 'Silver' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c8',
      workspace_id: defaultWorkspaceId,
      username: 'luxury.lucy',
      display_name: 'Luxury Lucy',
      points: 8900,
      avatar_url: 'https://i.pravatar.cc/150?u=luxury.lucy',
      tags: { membership_type: 'Gold' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b3c9',
      workspace_id: defaultWorkspaceId,
      username: 'elite.emma',
      display_name: 'Elite Emma',
      points: 15200,
      avatar_url: 'https://i.pravatar.cc/150?u=elite.emma',
      tags: { membership_type: 'Platinum' }
    },
    {
      customer_id: 'customer_123e4567b3c177308def018df490b310',
      workspace_id: defaultWorkspaceId,
      username: 'smart.sam',
      display_name: 'Smart Sam',
      points: 7400,
      avatar_url: 'https://i.pravatar.cc/150?u=smart.sam',
      tags: { membership_type: 'Gold' }
    }
  ]

  // Sample users representing different segments
  const segmentUsers: Customer[] = [
    {
      customer_id: 'customer_professional',
      workspace_id: defaultWorkspaceId,
      username: 'professional.user',
      display_name: 'Professional User',
      points: 25000,
      avatar_url: 'https://i.pravatar.cc/150?u=professional.user',
      tags: { 
        membership_type: 'Platinum',
        age: '35',
        segment: 'professional'
      }
    },
    {
      customer_id: 'customer_advanced',
      workspace_id: defaultWorkspaceId,
      username: 'advanced.user',
      display_name: 'Advanced User',
      points: 50000,
      avatar_url: 'https://i.pravatar.cc/150?u=advanced.user',
      tags: { 
        membership_type: 'Diamond',
        age: '42',
        segment: 'power_user'
      }
    }
  ]

  const defaultRewards: Reward[] = [
    {
      id: 'reward_123e4567b3c177308def018df490b3c1',
      workspace_id: defaultWorkspaceId,
      reward_id: 'reward_coffee_discount',
      name: 'Coffee Shop Discount',
      description: 'Get 25% off your next coffee purchase at participating locations.',
      points_required: 500,
      icon: 'cafe-outline',
      tags: {
        category: 'food_beverage',
        type: 'discount'
      }
    },
    {
      id: 'reward_123e4567b3c177308def018df490b3c2',
      workspace_id: defaultWorkspaceId,
      reward_id: 'reward_free_shipping',
      name: 'Free Shipping',
      description: 'Enjoy free shipping on your next online order, no minimum purchase required.',
      points_required: 750,
      icon: 'car-outline',
      tags: {
        category: 'shipping',
        type: 'benefit'
      }
    },
    {
      id: 'reward_123e4567b3c177308def018df490b3c3',
      workspace_id: defaultWorkspaceId,
      reward_id: 'reward_gift_card',
      name: '$10 Gift Card',
      description: 'Redeem for a $10 gift card to use on any future purchase.',
      points_required: 1000,
      icon: 'card-outline',
      tags: {
        category: 'gift_card',
        type: 'voucher'
      }
    },
    {
      id: 'reward_123e4567b3c177308def018df490b3c4',
      workspace_id: defaultWorkspaceId,
      reward_id: 'reward_premium_access',
      name: 'Premium Member Access',
      description: 'Unlock premium features and exclusive deals for 30 days.',
      points_required: 2000,
      icon: 'diamond-outline',
      tags: {
        category: 'membership',
        type: 'access'
      }
    },
    {
      id: 'reward_123e4567b3c177308def018df490b3c5',
      workspace_id: defaultWorkspaceId,
      reward_id: 'reward_birthday_bonus',
      name: 'Birthday Bonus',
      description: 'Special birthday discount - 50% off any item of your choice!',
      points_required: 1500,
      icon: 'gift-outline',
      tags: {
        category: 'special',
        type: 'discount'
      }
    }
  ]

  // Add some quest rewards for the advanced segment
  const questRewards: Reward[] = [
    {
      id: 'quest_123e4567b3c177308def018df490qst1',
      workspace_id: defaultWorkspaceId,
      reward_id: 'quest_daily_login',
      name: 'Daily Login Streak',
      description: 'Log in for 7 consecutive days to earn bonus points and unlock special rewards.',
      points_required: 0,
      points_awarded: 100,
      icon: 'calendar-outline',
      tags: {
        category: 'engagement',
        type: 'quest',
        quest_type: 'daily',
        progress: '3/7',
        target: '7'
      }
    },
    {
      id: 'quest_123e4567b3c177308def018df490qst2',
      workspace_id: defaultWorkspaceId,
      reward_id: 'quest_purchase_milestone',
      name: 'Purchase Milestone',
      description: 'Make 5 purchases this month to unlock exclusive member benefits.',
      points_required: 0,
      points_awarded: 500,
      icon: 'trophy-outline',
      tags: {
        category: 'purchase',
        type: 'quest',
        quest_type: 'monthly',
        progress: '2/5',
        target: '5'
      }
    },
    {
      id: 'quest_123e4567b3c177308def018df490qst3',
      workspace_id: defaultWorkspaceId,
      reward_id: 'quest_social_share',
      name: 'Social Sharing Challenge',
      description: 'Share 3 products on social media to earn bonus points and help others discover great deals.',
      points_required: 0,
      points_awarded: 250,
      icon: 'share-outline',
      tags: {
        category: 'social',
        type: 'quest',
        quest_type: 'weekly',
        progress: '1/3',
        target: '3'
      }
    }
  ]

  // Combine regular rewards and quests
  const allRewards = [...defaultRewards, ...questRewards]
  
  // Combine all customers for the leaderboard
  const allCustomers = [...defaultCustomers, ...segmentUsers]
  
  // Always reset the data on page load
  const leaderboardEntries: LeaderboardEntry[] = allCustomers
    .map(customer => ({
      rank: 0, // Will be calculated below
      customer_id: customer.customer_id,
      display_name: customer.display_name,
      score: customer.points || 0,
      achieved_at: new Date().toISOString() // Required by API spec
    }))
    .sort((a, b) => b.score - a.score)
    .map((entry, index) => ({
      ...entry,
      rank: index + 1
    }))

  // Set the default current user to professional segment
  const currentUser = segmentUsers.find(c => c.customer_id === 'customer_professional')

  // Store the data
  setItem('currentUser', currentUser)
  setItem('leaderboard', leaderboardEntries)
  setItem('rewards', allRewards)
  setItem('availableUsers', segmentUsers)
  setItem('customers', allCustomers)

  // Mark as initialized
  localStorage.setItem('demoDataInitialized', 'true')
  }
} 